import React, {useCallback, useEffect} from 'react';
import {generatePath, useRouteMatch} from 'react-router';
import {DATE_FORMAT_URL} from '../../../data/constants/datetime';
import meetingState from '../../../data/constants/meetingState';
import {isMeetingOverlaping} from '../../../data/filters/meetingFilters';
import {isSessionOverlaping} from '../../../data/filters/scheduleFilters';
import {useEventAvailabilitySlots} from '../../../data/hooks/availabilityHooks';
import {formatEventURLDate,  } from '../../../data/hooks/datetimeHooks';
import {useEvent} from '../../../data/hooks/eventHooks';
import {useI18n} from '../../../data/hooks/i18nHooks';
import {useMeetings} from '../../../data/hooks/meeting/useMeetings';
import { useUserSchedule } from '../../../data/hooks/schedule/useUserSchedule';
import {eventMomentFactory} from '../../../data/misc/eventMomentFactory';
import WaitWhileBusy from '../../WaitWhileBusy';
import {dialogConfirm} from '../../elements/Dialog/dialogs';
import Hint from '../../elements/Hint';
import Scrollable from '../../elements/Scrollable';
import TimeInterval from '../../elements/TimeInterval/TimeInterval';
import TimeslotSelection from '../../elements/TimeslotSelection/TimeslotSelection';
import TopBarEvent from '../../elements/TopBar/TopBarEvent';
import LoadMeetings from '../../loaders/LoadMeetings';
import LoadUserAvailability from '../../loaders/LoadUserAvailability';
import HasData from '../../validate/HasData';
import Screen from '../Screen';
import { useDatesFromItems } from '../../../data/hooks/datetime/useDatesFromItems';
import { useNextBestDate } from '../../../data/hooks/datetime/useNextBestDate';

const EventUserAvailability = props => {
    const {eventId, date: urlDate, returnURL} = props,
        event = useEvent(eventId),
        i18n = useI18n(),
        meetings = useMeetings(eventId),
        sessions = useUserSchedule(eventId),
        slots = useEventAvailabilitySlots(eventId),
        allDates = useDatesFromItems(event, slots),
        defaultDate = useNextBestDate(event, allDates),
        date = urlDate ? eventMomentFactory(event, urlDate, DATE_FORMAT_URL) : defaultDate,
        routeMatch = useRouteMatch();

    const handleDateClicked = useCallback(() => {
        app.history.replace(
            generatePath(routeMatch.path, {
                ...routeMatch.params,
                date: date.format(DATE_FORMAT_URL),
            })
        );
    }, [props.match]);

    const refresh = useCallback(async () => {
        await Promise.all([app.api.meetings.checkAvailability(eventId), app.api.events.fetchMeetings(eventId)]);
    }, [eventId]);

    useEffect(() => {
        refresh();
    }, []);

    const handleSlotClicked = useCallback(
        async slot => {
            // NOTE: logic copied from native app netwerkapp/ViewModels/ManageMeetingAvailabilityViewModel.cs#141 (title.mendatory-session-slot (MeetingSlot slot))
            if (slot.self_blocked) {
                const unblock = await dialogConfirm(
                    <>
                        {i18n['title.blocked-slot']}
                        <br />
                        {i18n['label.ask-to-unblock-slot']}
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.unblock'],
                    i18n['navigation.back']
                );
                if (unblock) {
                    const blockingMeetings = meetings.filter(meeting => isMeetingOverlaping(meeting, slot) && meeting.blocked && !meeting.cancelled);
                    app.store.dispatch(app.actions.availability.unblock(event.id, slot.start, slot.end));
                    await Promise.all(blockingMeetings.map(meeting => app.api.meetings.unblock(event.id, meeting.id)));
                    refresh();
                }
            } else if (slot.mendatory_session) {
                const sessionView = await dialogConfirm(
                    <>
                        {i18n['title.mendatory-session-slot']}
                        <br />
                        {i18n['message.mendatory-session-slot']}
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.view-sessions'],
                    i18n['navigation.back']
                );
                if (sessionView) {
                    var session = sessions.filter(p => isSessionOverlaping(p, slot) && p.mandatory)[0];
                    if (session) {
                        app.history.push(['/events', event.id, 'schedule', formatEventURLDate(event, session.start_local), session.id].join('/'));
                    }
                }
            } else if (!slot.self_available || (slot.self_busy && slot.type == 'meeting')) {
                const blockMeetings = meetings.filter(m => isMeetingOverlaping(slot, m) && m.state != meetingState.CANCELLED);

                let title = i18n['title.unavailable-slot'],
                    message = '';
                if (slot.type == 'speeddate') {
                    title = i18n['title.speeddate-session-slot'];
                } else if (slot.type == 'meeting') {
                    title = i18n['title.unavailable-slot'];
                    message = blockMeetings.find(m => m._state == meetingState.CONFIRMED)
                        ? i18n['message.meeting-slot-confirmed']
                        : i18n['message.meeting-slot-unconfirmed'];
                }
                let goview = await dialogConfirm(
                    <>
                        {title}
                        <br />
                        {message}
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.view-meetings'],
                    i18n['navigation.back']
                );
                if (goview) {
                    if (blockMeetings.length == 1) {
                        app.history.push(
                            ['/events', event.id, 'meetings', formatEventURLDate(event, blockMeetings[0].start_local), blockMeetings[0].id].join('/')
                        );
                    } else if (blockMeetings.length > 1) {
                        app.history.push(['/events', event.id, 'meetings', formatEventURLDate(event, slot.start_local)].join('/'));
                    }
                }
            } else if (slot.self_busy && slot.type != 'meeting') {
                const goview = await dialogConfirm(
                    i18n['title.session-slot'],
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.view-sessions'],
                    i18n['navigation.back']
                );
                if (goview) {
                    const programs = sessions.filter(s => isSessionOverlaping(s, slot) && s.display_type != 'speeddate');
                    if (programs.length == 1) {
                        app.history.push(['/events', event.id, 'schedule', formatEventURLDate(event, programs[0].start_local), programs[0].id].join('/'));
                    } else {
                        // NOTE: this case wasn't considered on the native app; it just pushed one view per session to the stack
                        app.history.push(['/events', event.id, 'schedule', formatEventURLDate(event, slot.start_local)].join('/'));
                    }
                }
            } else {
                const block = await dialogConfirm(
                    <>
                        {i18n['title.available-slot']}
                        <br />
                        {i18n['label.ask-to-block-slot']}
                    </>,
                    <TimeInterval event={event} slot={slot} />,
                    i18n['label.block'],
                    i18n['navigation.back']
                );
                if (block) {
                    app.store.dispatch(app.actions.availability.block(event.id, slot.start, slot.end));
                    await app.api.meetings.block(event.id, slot.start, slot.end);
                    refresh();
                }
            }
        },
        [event, meetings, sessions, refresh, i18n]
    );

    // const handleSlotClicked = useCallback(
    //     async slot => {
    //         if (slot.mendatory_session) {
    //             await dialogAlert(`${i18n['title.mendatory-session-slot']}\n${i18n['message.mendatory-session-slot']}`);
    //         } else if (slot.self_available) {
    //             if (await dialogConfirm(i18n['label.ask-to-block-slot'])) {
    //                 app.store.dispatch(app.actions.availability.block(event.id, slot.start, slot.end));
    //                 app.api.meetings.block(event.id, slot.start, slot.end).then(refresh);
    //             }
    //         } else {
    //             // find meeting
    //             let meeting = _(meetings).find({start: slot.start, end: slot.end});
    //             if (meeting) {
    //                 if (slot.self_blocked && meeting.blocked) {
    //                     if (await dialogConfirm(i18n['label.ask-to-unblock-slot'])) {
    //                         app.store.dispatch(app.actions.availability.unblock(event.id, slot.start, slot.end));
    //                         app.api.meetings
    //                             .unblock(event.id, meeting.id)
    //                             .then(result => {
    //                                 if (result.response) {
    //                                     app.store.dispatch(app.actions.availability.unblock(event.id, slot.start, slot.end));
    //                                 }
    //                             })
    //                             .then(refresh);
    //                     }
    //                 } else if (await dialogConfirm(i18n['label.ask-to-view-meeting'])) {
    //                     app.history.push(
    //                         ['/events', event.id, 'meetings', eventMomentFactory(event, slot.start_local).format(DATE_FORMAT_URL), meeting.id].join('/')
    //                     );
    //                 }
    //             }
    //         }
    //     },
    //     [event, meetings, refresh]
    // );

    const header = (
        <div>
            <TopBarEvent event={event} returnURL={returnURL}>
                {i18n['label.manage-availability']}
            </TopBarEvent>
            <hr />
        </div>
    );

    return (
        <Screen header={header}>
            <Scrollable>
                <Hint>{i18n['description.manage-availability']}</Hint>
                <TimeslotSelection
                    event={event}
                    slots={slots}
                    date={date}
                    onDateSelected={handleDateClicked}
                    onSlotSelected={handleSlotClicked}
                    selectedBackground={event.settings.secondary_color}
                    selectedColor={event.settings.block_text}
                />
            </Scrollable>
        </Screen>
    );
};

export default props => (
    <LoadUserAvailability eventId={props.match.params.eventId}>
        <LoadMeetings eventId={props.match.params.eventId}>
            <WaitWhileBusy>
                <HasData path={`events[${props.match.params.eventId}].meetings.loaded`}>
                    <EventUserAvailability
                        {...props.match.params}
                        returnURL={props.match.url.match(/availability$/) ? '..' : '../..'}
                        match={props.match}
                        key={props.match.params.date}
                    />
                </HasData>
            </WaitWhileBusy>
        </LoadMeetings>
    </LoadUserAvailability>
);
